/**
 * Delivery Service
 *
 * Service layer for delivery-related API calls.
 * Integrates with Google Maps APIs via Vercel serverless proxies for security.
 *
 * Architecture:
 * - All Google Maps API calls go through /api/* serverless functions
 * - API key stored server-side only, never exposed to frontend
 * - Rate limiting handled server-side
 */

// Use relative API endpoints - works with both local dev and production
const API_BASE_URL = '';

/**
 * Address autocomplete suggestion
 */
export interface AddressSuggestion {
  description: string;
  place_id: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

/**
 * Validated address result
 */
export interface ValidatedAddress {
  formatted_address: string;
  street_number?: string;
  street_name?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  lat: number;
  lng: number;
  place_id: string;
}

/**
 * Delivery price calculation result
 */
export interface DeliveryPrice {
  distance_km: number;
  distance_miles: number;
  base_price: number;
  per_km_price: number;
  total_price: number;
  estimated_duration_minutes: number;
}

/**
 * Get address autocomplete suggestions
 */
export async function getAddressAutocomplete(
  input: string,
  sessionToken?: string
): Promise<{ data: AddressSuggestion[] | null; error: string | null }> {
  try {
    const params = new URLSearchParams({
      input,
      ...(sessionToken && { sessiontoken: sessionToken }),
    });

    const response = await fetch(
      `${API_BASE_URL}/api/google-maps-autocomplete?${params}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch address suggestions');
    }

    const data = await response.json();

    // Map API response (camelCase) to interface format (snake_case)
    const suggestions = (data.suggestions || []).map((s: any) => ({
      placeId: s.placeId,
      place_id: s.placeId, // Add snake_case version for compatibility
      description: s.description,
      mainText: s.mainText,
      secondaryText: s.secondaryText,
      structured_formatting: {
        main_text: s.mainText,
        secondary_text: s.secondaryText,
      },
    }));

    return { data: suggestions, error: null };
  } catch (error) {
    console.error('Address autocomplete error:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Validate and geocode an address
 */
export async function validateAddress(
  placeId: string
): Promise<{ data: ValidatedAddress | null; error: string | null }> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/google-maps-validate-address`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placeId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Validation API error details:', errorData);
      throw new Error(
        errorData.message || errorData.error || 'Failed to validate address'
      );
    }

    const data = await response.json();

    // Map response fields to expected format
    return {
      data: {
        formatted_address: data.formattedAddress,
        street_number: data.streetNumber,
        street_name: data.streetName,
        city: data.city,
        state: data.state,
        zip_code: data.postalCode,
        country: data.country,
        lat: data.latitude,
        lng: data.longitude,
        place_id: data.placeId,
      },
      error: null,
    };
  } catch (error) {
    console.error('Address validation error:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Calculate delivery price based on distance
 */
export async function calculateDeliveryPrice(params: {
  originLat: number;
  originLng: number;
  destinationLat: number;
  destinationLng: number;
  locationId: string;
  trafficAware?: boolean;
}): Promise<{ data: DeliveryPrice | null; error: string | null }> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/calculate-delivery-price`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromLatitude: params.originLat,
          fromLongitude: params.originLng,
          toLatitude: params.destinationLat,
          toLongitude: params.destinationLng,
          locationId: params.locationId,
          trafficAware: params.trafficAware || false,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to calculate delivery price');
    }

    const data = await response.json();

    // Map response fields to expected format
    return {
      data: {
        distance_km: data.distanceKm,
        distance_miles: data.distanceKm * 0.621371, // Convert km to miles
        base_price: 0, // Not provided by new API
        per_km_price: 0, // Not provided by new API
        total_price: data.deliveryPrice,
        estimated_duration_minutes: data.durationMinutes,
      },
      error: null,
    };
  } catch (error) {
    console.error('Delivery price calculation error:', error);
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

/**
 * Parse location coordinates from pickup address
 * This is a helper function to extract coordinates from the location
 */
export function parseLocationCoordinates(_pickupAddress: string): {
  lat: number;
  lng: number;
} | null {
  // This would typically parse coordinates from the location's pickup_address field
  // For now, return null and require the location table to have lat/lng columns
  return null;
}
